import { Combobox } from "@base-ui/react/combobox";
import { Field } from "@base-ui/react/field";
import clsx from "clsx";
import type React from "react";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { vars } from "../../theme/theme.css";
import { FieldMessage } from "../FieldInfo/FieldMessage";
import { IconChevronDown, IconClose } from "../icons";
import { Spinner } from "../Spinner";
import {
  clearButtonClass,
  comboboxInputClass,
  comboboxInputSizeVariants,
  itemIndicatorClass,
  multiValueBadgeClass,
  noOptionsMessageClass,
  placeholderTextClass,
  SELECT_ITEM_HEIGHTS,
  selectItemClass,
  selectItemSizeVariants,
  selectLabelSizeVariants,
  selectPopupClass,
  selectPositionerClass,
  selectTriggerClass,
  selectTriggerSizeVariants,
  selectValueClass,
  selectValueSizeVariants,
  selectWrapperClass,
  triggerIndicatorsContainerClass,
  virtualizedListClass,
} from "./styles.css";
import type { CustomSelectProps, Option } from "./types";

const ROW_GAP = 4;
const OVERSCAN = 6;

function useVirtualWindow(itemCount: number, rowHeight: number) {
  const rowPitch = rowHeight + ROW_GAP;
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const countRef = useRef(itemCount);
  const [range, setRange] = useState({ start: 0, end: 0 });

  const listOffset = useCallback(() => {
    const scroller = scrollerRef.current;
    const list = listRef.current;
    if (!scroller || !list) return 0;
    return (
      list.getBoundingClientRect().top -
      scroller.getBoundingClientRect().top +
      scroller.scrollTop
    );
  }, []);

  const updateRange = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const top = scroller.scrollTop - listOffset();
    const start = Math.max(0, Math.floor(top / rowPitch) - OVERSCAN);
    const end = Math.min(
      countRef.current,
      start + Math.ceil(scroller.clientHeight / rowPitch) + OVERSCAN * 2,
    );
    setRange((prev) =>
      prev.start === start && prev.end === end ? prev : { start, end },
    );
  }, [listOffset, rowPitch]);

  useEffect(() => {
    countRef.current = itemCount;
    updateRange();
  });

  const attachScroller = useCallback(
    (el: HTMLDivElement | null) => {
      scrollerRef.current = el;
      if (el) updateRange();
    },
    [updateRange],
  );

  const attachList = useCallback((el: HTMLDivElement | null) => {
    listRef.current = el;
  }, []);

  const scrollIndexIntoView = (index: number) => {
    const scroller = scrollerRef.current;
    if (!scroller || index < 0) return;
    const top = listOffset() + index * rowPitch;
    const bottom = top + rowHeight;
    if (top < scroller.scrollTop) {
      scroller.scrollTop = top;
    } else if (bottom > scroller.scrollTop + scroller.clientHeight) {
      scroller.scrollTop = bottom - scroller.clientHeight;
    }
    updateRange();
  };

  return {
    range,
    attachScroller,
    attachList,
    updateRange,
    scrollIndexIntoView,
  };
}

export const VirtualizedSelect = forwardRef<
  HTMLButtonElement,
  CustomSelectProps
>(
  (
    {
      label,
      error,
      info,
      isDisabled,
      isLoading,
      isMulti,
      isClearable,
      isSearchable,
      options = [],
      containerStyles,
      className,
      placeholder = "Selecione",
      emptyMessage = "Nada encontrado",
      value: valueProp,
      onChange,
      onValueChange,
      id,
      size = "large",
    },
    ref,
  ) => {
    const currentValue =
      valueProp !== undefined ? valueProp : isMulti ? [] : null;

    const hasValue = isMulti
      ? Array.isArray(currentValue) && currentValue.length > 0
      : currentValue !== null && currentValue !== undefined;

    const handleValueChange = (newValue: any) => {
      onChange?.(newValue);
      onValueChange?.(newValue);
    };

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      handleValueChange(isMulti ? [] : null);
    };

    const labelOf = (v: any) =>
      options.find((opt) => opt.value === v)?.label ?? String(v ?? "");

    const [query, setQuery] = useState("");
    const filter = Combobox.useFilter({
      multiple: isMulti,
      value: currentValue,
    });

    const filteredOptions: Option[] =
      isSearchable && query
        ? options.filter((opt) => filter.contains(opt.value, query, labelOf))
        : options;

    const rowHeight = SELECT_ITEM_HEIGHTS[size];
    const rowPitch = rowHeight + ROW_GAP;
    const {
      range,
      attachScroller,
      attachList,
      updateRange,
      scrollIndexIntoView,
    } = useVirtualWindow(filteredOptions.length, rowHeight);

    const handleItemHighlighted = (
      highlightedValue: any,
      details: { reason: string },
    ) => {
      if (details.reason === "pointer" || highlightedValue === undefined)
        return;
      scrollIndexIntoView(
        filteredOptions.findIndex((opt) => opt.value === highlightedValue),
      );
    };

    const indicators = (
      <>
        {isClearable && hasValue && (
          <button
            type="button"
            className={clearButtonClass}
            style={{ pointerEvents: "all" }}
            onClick={handleClear}
          >
            <IconClose />
          </button>
        )}
        {isLoading ? (
          <Spinner color={vars.colors.neutral.light} size={16} />
        ) : (
          <IconChevronDown className="custom-dropdown-icon" />
        )}
      </>
    );

    return (
      <Field.Root
        className={clsx(selectWrapperClass, className)}
        style={containerStyles}
        invalid={!!error}
        data-slot="select-root"
      >
        {label && (
          <Field.Label
            className={selectLabelSizeVariants[size]}
            data-slot="select-label"
          >
            {label}
          </Field.Label>
        )}
        <Combobox.Root
          virtualized
          items={options.map((opt) => opt.value)}
          filteredItems={filteredOptions.map((opt) => opt.value)}
          value={currentValue}
          onValueChange={handleValueChange}
          onInputValueChange={isSearchable ? setQuery : undefined}
          onItemHighlighted={handleItemHighlighted}
          multiple={isMulti}
          disabled={isDisabled || isLoading}
          itemToStringLabel={labelOf}
          id={id}
        >
          {isSearchable ? (
            <div style={{ position: "relative", width: "100%" }}>
              <Combobox.Input
                className={clsx(
                  comboboxInputClass,
                  comboboxInputSizeVariants[size],
                )}
                placeholder={placeholder}
                autoComplete="off"
                style={{ paddingRight: "40px" }}
              />
              <div
                className={triggerIndicatorsContainerClass}
                data-size={size}
                style={{
                  position: "absolute",
                  right: 0,
                  top: 0,
                  height: "100%",
                  pointerEvents: "none",
                }}
              >
                {indicators}
              </div>
            </div>
          ) : (
            <Combobox.Trigger
              ref={ref}
              className={clsx(
                selectTriggerClass,
                selectTriggerSizeVariants[size],
              )}
              data-slot="select-trigger"
            >
              <span
                className={clsx(
                  selectValueClass,
                  selectValueSizeVariants[size],
                )}
              >
                <Combobox.Value>
                  {(value: any) => {
                    const isEmpty =
                      value === null ||
                      value === undefined ||
                      (isMulti && Array.isArray(value) && value.length === 0);

                    if (isEmpty) {
                      return (
                        <span className={placeholderTextClass}>
                          {placeholder}
                        </span>
                      );
                    }

                    if (isMulti && Array.isArray(value)) {
                      return options
                        .filter((opt) => value.includes(opt.value))
                        .map((opt) => (
                          <span
                            key={opt.value}
                            className={multiValueBadgeClass}
                          >
                            {opt.label}
                          </span>
                        ));
                    }

                    return labelOf(value);
                  }}
                </Combobox.Value>
              </span>
              <div className={triggerIndicatorsContainerClass} data-size={size}>
                {indicators}
              </div>
            </Combobox.Trigger>
          )}
          <Combobox.Portal>
            <Combobox.Positioner
              className={selectPositionerClass}
              sideOffset={4}
            >
              <Combobox.Popup
                className={selectPopupClass}
                data-slot="select-popup"
                ref={attachScroller}
                onScroll={updateRange}
              >
                <Combobox.Empty className={noOptionsMessageClass}>
                  {emptyMessage}
                </Combobox.Empty>
                <Combobox.List
                  ref={attachList}
                  className={virtualizedListClass}
                  data-slot="select-list"
                  style={{
                    height: Math.max(
                      0,
                      filteredOptions.length * rowPitch - ROW_GAP,
                    ),
                  }}
                >
                  {filteredOptions
                    .slice(range.start, range.end)
                    .map((opt, i) => (
                      <Combobox.Item
                        key={opt.value}
                        value={opt.value}
                        index={range.start + i}
                        disabled={opt.isDisabled}
                        className={clsx(
                          selectItemClass,
                          selectItemSizeVariants[size],
                        )}
                        style={{
                          position: "absolute",
                          top: (range.start + i) * rowPitch,
                          left: 0,
                          right: 0,
                          height: rowHeight,
                        }}
                      >
                        {opt.label}
                        <Combobox.ItemIndicator
                          className={itemIndicatorClass}
                          keepMounted
                        >
                          ✓
                        </Combobox.ItemIndicator>
                      </Combobox.Item>
                    ))}
                </Combobox.List>
              </Combobox.Popup>
            </Combobox.Positioner>
          </Combobox.Portal>
        </Combobox.Root>
        <FieldMessage
          error={error}
          info={info}
          isDisabled={isDisabled || isLoading}
        />
      </Field.Root>
    );
  },
);

VirtualizedSelect.displayName = "VirtualizedSelect";
